export const sustainabilityPage = {
  audio: {
    bgMusic: 'Background music',
    mainTitle: 'Sustainability · Grounding · Organic Living',
  },
  materialsSection: {
    label: 'Our Materials',
    title: 'From Nature, Back to Nature',
    subtitle: 'Every material is concrete proof of our respect for the planet and your health.',
    allLabel: 'All Our Sustainable Materials',
    allSubtitle: 'Each one is a nature-inspired, eco-conscious choice',
    target: 'Our Goal',
  },
  manifesto: {
    label: 'Our Manifesto',
    title: 'Why Do We Exist?',
    p1: 'The fashion industry is the second largest water consumer and one of the biggest chemical polluters in the world. Every year 92 million tons of textile waste is generated, most of which sits in landfills for centuries without decomposing.',
    p2: 'FentyBarefoot was founded to stand against this. Our organic fashion philosophy is not just about using organic materials — it means placing environmental and social responsibility at the core of every step from production to packaging, logistics to product lifespan.',
    p3: 'We bring together Anatolia\'s millennia-old craft tradition with modern sustainability science. Every product, colored with botanical dyes, processed with vegetable tannins, and brought to life by skilled hands, is a concrete expression of our respect for our planet and the people who inhabit it.',
    zeroBadge: '100% Carbon Neutral as of 2024',
    carbonLabel: 'Carbon Footprint',
    carbonVal: 'Zero Emissions',
  },
  organicCotton: {
    label: 'Organic Cotton Dresses',
    title: 'Every Touch on Your Skin',
    titleHighlight: 'Comes From Nature',
    p1: 'Our organic cotton dresses are woven with yarns sourced from GOTS-certified farms in the Aegean and Çukurova regions. Fabrics woven on traditional looms are colored with botanical dyes and shaped with hand stitching.',
    p2: 'Why organic cotton? Conventional cotton production consumes 16% of the world\'s agricultural pesticides. Organic cotton goes through a production process that is completely free of these chemicals, nourishing the soil and protecting biodiversity.',
    p3: 'Every stitch in our dresses is made by artisans who carry on Anatolia\'s deep-rooted tailoring tradition. Hand stitching, which replaces machine stitching, creates a product that is both more durable and repairable.',
    f1: '91% Less Water',
    f2: 'Zero Pesticides',
    f3: 'Hypoallergenic',
    f4: 'Biodegradable',
  },
  veganLeather: {
    label: 'Vegan & Vegetable Leather',
    title: 'Leather Without Chemicals',
    titleHighlight: 'Possible? Yes.',
    subtitle: 'Two revolutionary approaches that reduce the chemical burden of the traditional leather industry to zero: the ancient wisdom of vegetable tanning and the power of next-generation biomaterials.',
    card1Label: 'The Secret of Vegetable Tanning',
    card1Text: 'Natural tannins from oak bark, chestnut shell, and mimosa flower process the leather slowly and carefully. This method is a 3,000-year-old tradition that leaves virtually zero chemical waste compared to chrome tanning. Vegetable-tanned leather develops a beautiful patina over time, breathes, and lasts for decades.',
    card2Label: 'Cactus Leather: Revolution from the Desert',
    card2Text: 'Desserto® leather, made from the leaves of the Nopal cactus grown in Zacatecas, Mexico, requires no irrigation, no fertilizer, and the harvested leaves are collected without harming the cactus itself. Solar energy is used in the production process.',
    card3Label: 'Mushroom Leather: Gift of the Forest',
    card3Text: 'Mylo™ mushroom leather is made from mushroom mycelium fed on forest waste. This carbon-negative material mimics the texture and flexibility of traditional leather while being completely biodegradable.',
  },
  botanicalDyes: {
    label: 'Botanical Dyes',
    title: 'Color, Nature\'s',
    titleHighlight: 'Own Palette',
    p1: 'Synthetic dyes are responsible for 20% of global water pollution. Heavy metals and toxic chemicals in dyeing wastewater mix with rivers and groundwater, destroying the ecosystem.',
    p2: 'The botanical dyes we use at FentyBarefoot are derived entirely from natural sources such as indigo plant, turmeric root, pomegranate peel, onion skin, cranberry, woad and sumac. Each dye bath transfers the essence of a botanical garden to the fabric.',
    p3: 'Botanical dyeing gives every product a unique and unrepeatable hue. Even two products from the same dye bath are slightly different from each other.',
    plant1: 'Indigo Plant',
    plant1Color: 'Deep Blue & Indigo',
    plant2: 'Turmeric Root',
    plant2Color: 'Golden Yellow & Mustard',
    plant3: 'Pomegranate Peel',
    plant3Color: 'Terracotta & Orange',
    plant4: 'Cranberry',
    plant4Color: 'Burgundy & Brick',
  },
  handcraft: {
    label: 'Handcraft & Art',
    title: 'Every Stitch a Story,',
    titleHighlight: 'Every Product a Work of Art',
    subtitle: 'We bring together Anatolia\'s millennia-old craft tradition with modern sustainability science.',
    step1Title: 'Material Selection',
    step1Desc: 'Every piece of leather and every cotton thread is individually inspected and selected. No defective material is used.',
    step2Title: 'Pattern & Cutting',
    step2Desc: 'Pieces cut by hand using traditional wooden lasts are shaped with a zero-waste goal.',
    step3Title: 'Hand Stitching',
    step3Desc: 'Saddle stitch hand stitching is 3 times more durable than machine stitching.',
    step4Title: 'Finishing & Quality',
    step4Desc: 'Every product goes through the master\'s final approval. Special attention is given to repairability.',
    quoteText: 'When making a pair of shoes, I think about every step of the person who will wear them. Every stitch is placed so that step is safe and comfortable.',
    quoteAuthor: 'Mehmet, Master Craftsman',
    quoteRole: '42 Years of Leather Craft · İzmir',
  },
  certsSection: {
    label: 'Certifications & Standards',
    title: 'Proof of Our Credibility',
    subtitle: 'Our sustainability claims are not empty words — they are based on standards audited and certified by independent international bodies.',
  },
  timelineSection: {
    label: 'Our Sustainability Journey',
    title: 'Better Every Year',
  },
  faqSection: {
    label: 'Frequently Asked Questions',
    title: 'What You Wonder',
  },
  cta: {
    title: 'Sustainable Fashion\nIs a Matter of Choice',
    subtitle: 'Every purchasing decision you make shapes the future of our planet. Reflect both your style and your values with our organic, botanical and handmade products.',
    cta1: 'Explore Organic Collection',
    cta2: 'Read the Blog',
  },
  materials: [
    {
      title: 'Organic Cotton',
      subtitle: 'GOTS Certified · Chemical-Free',
      desc: 'Organic cotton consumes 91% less water compared to conventional cotton production and is grown without any synthetic pesticides or fertilizers. Every cotton yarn we use at FentyBarefoot is sourced from GOTS-certified farms. This material, preferred in our products that come into direct contact with skin, stands out for its breathable structure, hypoallergenic properties, and longevity.',
      tags: ['GOTS Certified', 'Pesticide-Free', 'Hypoallergenic', 'Breathable'],
      statLabel: 'Less Water',
    },
    {
      title: 'Vegetable Leather',
      subtitle: 'Processed with Natural Tannins · Chemical-Free',
      desc: 'Vegetable tanning uses natural tannins from oak bark, mimosa shell, chestnut, and sumac. This traditional method leaves virtually zero chemical waste compared to chrome tanning. FentyBarefoot\'s vegetable leather collection comes from Turkey\'s long-established leather artisans. This leather develops a beautiful patina over time and offers an experience far beyond synthetic alternatives.',
      tags: ['Vegetable Tannin', 'Chrome-Free', 'Develops Patina', 'Long-Lasting'],
      statLabel: 'Chemical Waste',
    },
    {
      title: 'Vegan Leather',
      subtitle: 'Cactus · Mushroom · Pineapple Fiber',
      desc: 'Vegan leather is no longer just plastic-based PU. FentyBarefoot integrates next-generation biomaterials such as Desserto® from cactus leaves, Mylo™ from mushroom mycelium, and Piñatex® from pineapple fiber. These materials protect animal welfare while eliminating petrochemical dependence.',
      tags: ['Cactus Leather', 'Mushroom Leather', 'Piñatex®', 'Animal-Friendly'],
      statLabel: 'Animal-Friendly',
    },
    {
      title: 'Botanical Dyes',
      subtitle: 'Indigo · Turmeric · Woad · Root Dye',
      desc: 'Synthetic dyes are responsible for 20% of global water pollution. The botanical dyes we use at FentyBarefoot are derived from entirely natural sources such as indigo plant, turmeric root, pomegranate peel, onion skin, and cranberry. This dyeing process gives each product a unique and unrepeatable hue.',
      tags: ['Indigo Dye', 'Turmeric', 'Pomegranate Peel', 'Zero Chemical'],
      statLabel: 'Less Pollution',
    },
    {
      title: 'Handcraft & Art',
      subtitle: 'Traditional Craftsmanship · Zero Mass Production',
      desc: 'Every FentyBarefoot product is made by artisans who carry on Anatolia\'s deep-rooted craft tradition. Hand stitching gives each product a unique character and ensures repairability. Handcraft works with a zero-waste goal, unlike mass production; every piece of leather, every thread, and every buckle is carefully utilized.',
      tags: ['Hand Stitched', 'Anatolian Craftsman', 'Repairable', 'Zero Waste'],
      statLabel: 'Handmade',
    },
  ],
  certifications: [
    { title: 'GOTS Certificate', desc: 'Global Organic Textile Standard — the highest international standard for organic textile production.' },
    { title: 'OEKO-TEX® Standard 100', desc: 'Every product component has been tested and certified for harmful substances.' },
    { title: 'B Corp Certified', desc: 'We meet standards of social and environmental performance, accountability, and transparency.' },
    { title: 'Carbon Neutral', desc: 'Since 2023, we have achieved carbon neutral status in all our production and logistics processes.' },
    { title: 'Cruelty Free', desc: 'No animal testing is conducted on any of our products; we prioritize vegan and botanical alternatives.' },
    { title: 'Fair Trade Production', desc: 'All our production partners comply with fair wages, safe working conditions, and ethical business standards.' },
  ],
  timeline: [
    { year: '2018', title: 'Transition to Organic Cotton', desc: 'Transition to GOTS-certified organic cotton completed across all cotton products.' },
    { year: '2019', title: 'Return to Vegetable Tanning', desc: 'Complete abandonment of chrome tanning and return to traditional vegetable tannin methods.' },
    { year: '2020', title: 'Vegan Collection Launch', desc: 'First vegan collection using cactus and mushroom leather was launched.' },
    { year: '2021', title: 'Zero Waste Packaging', desc: 'All packaging converted to recycled paper and biodegradable materials.' },
    { year: '2022', title: 'Solar Energy Workshop', desc: 'Production workshops began operating entirely on renewable solar energy.' },
    { year: '2023', title: 'Carbon Neutral Status', desc: 'Carbon neutral status achieved across the entire supply chain and logistics operations.' },
    { year: '2024', title: 'Craft Academy', desc: 'A craftsmanship academy was founded to carry Anatolia\'s traditional leather artisans into the future.' },
  ],
  faqs: [
    {
      q: 'What is the difference between vegetable leather and regular leather?',
      a: 'Vegetable leather uses natural tannins from oak bark, chestnut shell, and mimosa flower to process the hide slowly and carefully. This 3,000-year-old tradition contains no heavy metals unlike chrome tanning. Vegetable-tanned leather develops a beautiful patina over time, breathes, and lasts for decades.',
    },
    {
      q: 'Is vegan leather more durable than real leather?',
      a: 'Next-generation vegan leathers based on cactus and mushroom are at a level that can compete with traditional leather in terms of durability. Piñatex® (pineapple fiber) shows superior performance in tear resistance. All our vegan leather products come with a 2-year warranty.',
    },
    {
      q: "Don't botanically dyed products fade?",
      a: 'Botanical dyes do not fade like synthetic dyes; on the contrary, they develop deeper and richer tones over time. With proper care, botanically dyed products maintain their beauty for decades.',
    },
    {
      q: 'Why are handmade products more valuable than mass production?',
      a: 'In handmade production, each piece is processed individually; this means zero waste and maximum quality control. Since handmade products are repairable, their lifespan can be 3-5 times longer than mass-produced items.',
    },
    {
      q: 'Why is organic cotton more expensive?',
      a: 'Organic cotton has higher costs due to certification processes, lower yields, and labor-intensive harvesting methods. However, considering its long-term contribution to skin health and product longevity, its true value is much higher.',
    },
  ],
  noWar: {
    badge: 'A Stand for Peace',
    title: 'No to War,\nYes to Life',
    subtitle: 'We do not support any form of human death. We stand against chemical weapons, every action that disrupts nature\'s balance, and everything that harms all living beings.',
    p1: 'Sustainability is not just about material choices. True sustainability requires standing against every harm that humans inflict on each other and on nature. That is why we openly declare our opposition to war, violence, and chemical weapons.',
    p2: 'Chemical weapons pollute soil, water, and air. They cause generational illness, land degradation, and loss of biodiversity. Opposing these tools that disrupt the atmosphere, destroy Earth\'s balance, and strip living beings of their right to life is an inseparable part of our respect for our planet.',
    p3: 'Earth\'s balance requires people living in peace, protected nature, and sustainable production to coexist. At FentyBarefoot, we carry this belief in every product and work to leave a peaceful and green world for future generations.',
    stat1: 'Anti-War',
    stat2: 'Against Chemical Weapons',
    stat3: 'Nature Defender',
    stat4: 'Life Advocate',
    quoteText: 'There is no sustainability without peace. Standing against everything that harms humanity and nature is both an ethical and ecological responsibility.',
  },
};
